import type { ReactNode } from 'react'
import { FieldHelp } from '@speaking-coach/ui'

export const CONTENT_HELP = {
  targetSlots:
    'Every target-vocabulary placement in a lesson counts as one slot. The same lemma in two lessons counts as two slots.',
  uniqueLemmas:
    'The number of distinct canonical dictionary forms. Repeated use of one lemma still counts once here.',
  gradeDistribution:
    'Each chip is the CEFR grade assigned to target slots at this level. Counts are slot occurrences, not unique lemmas; select a chip to inspect them.',
  aboveBandTargetSlots:
    'Target slots graded above this level’s allowed vocabulary window. Registered exceptions still count in the rate; the percentage uses all target slots at the level.',
  hardCapBreaches:
    'Measured learner-facing sentences longer than this level’s hard maximum. Sentences above guidance but within the maximum are not counted here.',
  sentencePercentiles:
    'Percentiles summarize measured learner-facing sentence length. For example, p90 means 90% of sentences are that many words or shorter.',
  sample:
    'The number of measurable learner-facing sentences used for this level summary.',
  advisoryAndHardCaps:
    'The first number is writing guidance; the second is the hard maximum. A sentence is flagged only when it is longer than the relevant number.',
  aboveCaps:
    'Advisory counts sentences longer than guidance. Hard counts sentences longer than the maximum and is a stricter subset.',
  sentenceMaximum:
    'The longest measured learner-facing sentence in this lesson or level.',
  quoteIntegrity:
    'Sentence statistics are trustworthy only when learner-facing quoted text can be paired and measured cleanly.',
  missions:
    'The first number is the lesson’s mission count; the second is the count required by the selected level contract.',
  expectedCapstone:
    'The final integrated task shape required by the level contract, including whether prior skills must be modeled first.',
  aboveBandTargets:
    'All target slots graded above this lesson level’s allowed window. They count toward the vocabulary quota only when covered by an approved exception; the value compares all above-band slots with the per-lesson cap.',
  cefrGrade:
    'The external CEFR grade assigned to the canonical lemma, which may differ from the dashboard level being reviewed.',
  bandRelationship:
    'Compares the lemma’s CEFR grade with the vocabulary window allowed for the selected learner level.',
  exceptionState:
    'Registered means an out-of-window use has an approved justification. Unregistered means the required justification is missing.',
  reuse:
    'Shows whether the canonical lemma appears once or is also used in another lesson or Program level.',
  inBand:
    'Target slots whose lemma grade is inside the vocabulary window allowed for this learner level.',
  belowBand:
    'Target slots graded below this level’s vocabulary window. They may be intentional, but remain visible for review.',
  aboveBand:
    'Target slots graded above this level’s vocabulary window, including uses with approved exceptions.',
  ungraded:
    'Target slots whose canonical lemma has no CEFR grade in the configured word list.',
  unregistered:
    'Out-of-window target slots that do not have the required approved exception.',
  repeated:
    'Distinct canonical lemmas used by more than one lesson or Program level. This is a lemma count, not a slot count.',
  levelGuidelines:
    'Only rules applicable to the selected learner level are shown. A1 uses the four-mission contract; A2–B2 use the seven-mission contract.',
} as const

export function sentencePercentileHelp(percentile: number): string {
  return `${percentile}% of measured learner-facing sentences are this many words or shorter.`
}

export function expectedComplicationsHelp(
  level: string,
  expectedComplications: string,
): string {
  return `The extra constraints required after guided practice for ${level}: ${expectedComplications}.`
}

export function ContentMetricLabel({
  label,
  help,
  uppercase = false,
}: {
  label: string
  help?: ReactNode
  uppercase?: boolean
}): JSX.Element {
  const className = uppercase
    ? 'text-label font-medium uppercase tracking-wide text-text-secondary'
    : 'text-caption text-text-secondary'

  return (
    <div className="pointer-events-none flex min-h-5 items-center gap-1">
      <p className={className}>{label}</p>
      {help !== undefined ? (
        <span className="pointer-events-auto">
          <FieldHelp label={label.toLowerCase()} size="compact">
            {help}
          </FieldHelp>
        </span>
      ) : null}
    </div>
  )
}
